import { useState, useMemo, memo } from "react";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Clock,
  CheckCircle2,
  Star,
  DollarSign,
  Globe,
  Download,
  Calendar,
  Filter,
  Bot,
  ChevronDown,
  Activity,
  Zap,
  Users,
  FileText,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  messagesLast30Days,
  messagesLast7Days,
  responseTimeTrend,
  responseTimeTrend7Days,
  channelDistribution,
  topIntents,
  kpiSummary,
  hourlyDistribution,
  weeklyComparison,
} from "../data/analyticsData";
import { exportChartData } from "../utils/exportData";
import ReportBuilder from "./ReportBuilder";
import TeamMetrics from "./TeamMetrics";

import { useTranslation } from 'react-i18next';
// ─── Helpers ───
const formatNumber = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const formatCurrency = (n) => `$${n.toLocaleString()}`;

const TrendIndicator = ({ value, suffix = "%", invert = false }) => {
  const isPositive = invert ? value < 0 : value > 0;
  const color = isPositive ? "text-emerald-400" : "text-red-400";
  const Icon = isPositive ? TrendingUp : TrendingDown;
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="font-mono text-xs font-bold">
        {isPositive ? "+" : ""}
        {value}{suffix}
      </span>
    </div>
  );
};

// ─── Skeleton Loader ───
function SkeletonCard() {
    const { t } = useTranslation();
return (
    <div className="border border-zinc-800 bg-zinc-950/30 p-5 animate-pulse">
      <div className="h-3 bg-zinc-800 rounded w-24 mb-3" />
      <div className="h-8 bg-zinc-800 rounded w-32 mb-2" />
      <div className="h-3 bg-zinc-800 rounded w-20" />
    </div>
  );
}

// ─── KPI Card ───
function KPICard({ icon: Icon, label, value, sublabel, trend, trendSuffix = "%", trendInverse = false, accent = "text-amber-500", loading }) {
  if (loading) return <SkeletonCard />;
  return (
    <div className="border border-zinc-800 bg-zinc-950/30 p-5 hover:border-zinc-700 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <Icon className={`w-5 h-5 ${accent}`} />
      </div>
      <div className="font-display text-2xl md:text-3xl text-white mb-1">{value}</div>
      <div className="flex items-center justify-between">
        {sublabel && <span className="font-mono text-[10px] text-zinc-500">{sublabel}</span>}
        {trend !== undefined && <TrendIndicator value={trend} suffix={trendSuffix} invert={trendInverse} />}
      </div>
    </div>
  );
}

// ─── Custom Tooltip ───
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 p-3 shadow-xl rounded-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-zinc-300">{entry.name}:</span>
          <span className="font-mono font-bold text-white">{formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Period Selector ───
function PeriodSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
  ];
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 bg-zinc-950/50 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors"
      >
        <Calendar className="w-3.5 h-3.5 text-amber-500" />
        {selected?.label}
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 border border-zinc-800 bg-zinc-950 shadow-xl z-10 min-w-[160px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                value === opt.value ? "text-amber-500 bg-amber-500/5" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Channel Filter ───
function ChannelFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [
    { value: "all", label: "All Channels" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "telegram", label: "Telegram" },
    { value: "email", label: "Email" },
    { value: "discord", label: "Discord" },
    { value: "slack", label: "Slack" },
  ];
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 bg-zinc-950/50 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors"
      >
        <Filter className="w-3.5 h-3.5 text-amber-500" />
        {selected?.label}
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 border border-zinc-800 bg-zinc-950 shadow-xl z-10 min-w-[160px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                value === opt.value ? "text-amber-500 bg-amber-500/5" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Export Menu (per-chart) ───
function ExportMenu({ data, chartName, period, channel }) {
  const [open, setOpen] = useState(false);
  const filters = { period, channel };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-zinc-500 hover:text-amber-500 transition-colors"
        title={t('analytics_dashboard.export_data')}
      >
        <Download className="w-3.5 h-3.5" />
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 border border-zinc-800 bg-zinc-950 shadow-xl z-10 min-w-[120px]">
          {["csv", "json", "pdf"].map((fmt) => (
            <button
              key={fmt}
              onClick={() => { exportChartData(data, chartName, fmt, filters); setOpen(false); }}
              className="w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400 hover:text-amber-500 hover:bg-zinc-900 transition-colors"
            >
              Export {fmt.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Toast Notification ───
function Toast({ message, visible }) {
  if (!visible) return null;
  return (
    <div className="fixed top-4 right-4 z-50 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 font-mono text-xs uppercase tracking-[0.1em] shadow-xl">
      {message}
    </div>
  );
}

// ─── Section Label ───
function SectionLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
      {children}
    </div>
  );
}

// ─── Main Component ───
export default memo(function AnalyticsDashboard() {
  const [period, setPeriod] = useState("30d");
  const [channel, setChannel] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'reports' | 'team'

  // Simulate loading
  useState(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Filter data based on period
  const messageData = useMemo(() => {
    const data = period === "7d" ? messagesLast7Days : messagesLast30Days;
    return data.map((d) => ({
      ...d,
      label: period === "7d" ? d.day : `${d.date.split("-")[2]}/${d.date.split("-")[1]}`,
    }));
  }, [period]);

  const responseData = useMemo(() => {
    const data = period === "7d" ? responseTimeTrend7Days : responseTimeTrend;
    return data.map((d) => ({
      ...d,
      label: period === "7d" ? d.day : `${d.date.split("-")[2]}/${d.date.split("-")[1]}`,
    }));
  }, [period]);

  // Channel-filtered data (simulated by scaling)
  const channelMultiplier = useMemo(() => {
    if (channel === "all") return 1;
    const map = { whatsapp: 0.42, telegram: 0.28, email: 0.18, discord: 0.08, slack: 0.04 };
    return map[channel] || 1;
  }, [channel]);

  const filteredMessageData = useMemo(() => {
    if (channel === "all") return messageData;
    return messageData.map((d) => ({
      ...d,
      messages: Math.round(d.messages * channelMultiplier),
      received: Math.round(d.received * channelMultiplier),
      sent: Math.round(d.sent * channelMultiplier),
    }));
  }, [messageData, channel, channelMultiplier]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-zinc-800 bg-zinc-950/30 p-5 h-80 animate-pulse">
            <div className="h-3 bg-zinc-800 rounded w-32 mb-4" />
            <div className="h-64 bg-zinc-900/50 rounded" />
          </div>
          <div className="border border-zinc-800 bg-zinc-950/30 p-5 h-80 animate-pulse">
            <div className="h-3 bg-zinc-800 rounded w-32 mb-4" />
            <div className="h-64 bg-zinc-900/50 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header + Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <SectionLabel>// ANALYTICS DASHBOARD</SectionLabel>
          <h2 className="font-display text-2xl md:text-3xl uppercase">{t('analytics_dashboard.performance_overview')}</h2>
          <p className="text-zinc-500 text-sm mt-1">{t('analytics_dashboard.real_time_metrics_and_kpis_for_your_ai_agents')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeTab === "overview" && (
            <>
              <PeriodSelector value={period} onChange={setPeriod} />
              <ChannelFilter value={channel} onChange={setChannel} />
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border border-zinc-800 p-1 w-fit">
        {[
          { key: "overview", label: "Overview", icon: Activity },
          { key: "reports", label: "Custom Reports", icon: FileText },
          { key: "team", label: "Team", icon: Users },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              activeTab === key
                ? "bg-amber-500/10 text-amber-500 border border-amber-500/30"
                : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab: Custom Reports */}
      {activeTab === "reports" && (
        <ReportBuilder />
      )}

      {/* Tab: Team Metrics */}
      {activeTab === "team" && (
        <TeamMetrics period={period} channel={channel} />
      )}

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          icon={MessageSquare}
          label={t('analytics_dashboard.messages_today')}
          value={formatNumber(Math.round(kpiSummary.messagesToday * channelMultiplier))}
          sublabel="vs yesterday"
          trend={kpiSummary.messagesTodayTrend}
          accent="text-sky-400"
        />
        <KPICard
          icon={Clock}
          label={t('analytics_dashboard.avg_response')}
          value={`${kpiSummary.avgResponseTime}s`}
          sublabel="p95: 2.8s"
          trend={kpiSummary.avgResponseTrend}
          trendSuffix="s"
          trendInverse
          accent="text-amber-500"
        />
        <KPICard
          icon={CheckCircle2}
          label={t('analytics_dashboard.auto_resolution')}
          value={`${kpiSummary.autoResolutionRate}%`}
          sublabel={`${kpiSummary.escalatedCount} escalated`}
          trend={kpiSummary.autoResolutionTrend}
          accent="text-emerald-500"
        />
        <KPICard
          icon={Star}
          label={t('analytics_dashboard.nps_score')}
          value={kpiSummary.npsScore}
          sublabel={`${kpiSummary.satisfactionRate}% satisfied`}
          trend={kpiSummary.npsTrend}
          trendSuffix=" pts"
          accent="text-yellow-500"
        />
        <KPICard
          icon={DollarSign}
          label={t('analytics_dashboard.cost_saved')}
          value={formatCurrency(Math.round(kpiSummary.costSaved * channelMultiplier))}
          sublabel={`${Math.round(kpiSummary.hoursSaved * channelMultiplier)}h saved`}
          trend={kpiSummary.costSavedTrend}
          accent="text-emerald-400"
        />
        <KPICard
          icon={Globe}
          label={t('analytics_dashboard.top_channel')}
          value="WhatsApp"
          sublabel="42% of messages"
          accent="text-green-500"
        />
      </div>

      {/* Charts Row 1: Message Volume + Channel Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Chart: Message Volume */}
        <div className="lg:col-span-2 border border-zinc-800 bg-zinc-950/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.message_volume')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">
                Messages over time • {period === "7d" ? "Last 7 days" : "Last 30 days"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={filteredMessageData} chartName="Message Volume" period={period} channel={channel} />
              <Activity className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={filteredMessageData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                tickFormatter={formatNumber}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}
              />
              <Line
                type="monotone"
                dataKey="messages"
                name={t('analytics_dashboard.total')}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: "#f59e0b" }}
              />
              <Line
                type="monotone"
                dataKey="received"
                name={t('analytics_dashboard.received')}
                stroke="#0ea5e9"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: "#0ea5e9" }}
              />
              <Line
                type="monotone"
                dataKey="sent"
                name={t('analytics_dashboard.sent')}
                stroke="#10b981"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: "#10b981" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart: Channel Distribution */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-5">
            <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.by_channel')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">{t('analytics_dashboard.distribution')}</p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={channelDistribution} chartName="Channel Distribution" period={period} channel={channel} />
              <Globe className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={channelDistribution}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {channelDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-zinc-900 border border-zinc-700 p-2 shadow-xl rounded-sm">
                      <div className="font-mono text-xs text-white">{d.name}: {d.value}%</div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {channelDistribution.map((ch) => (
              <div key={ch.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ch.color }} />
                  <span className="font-mono text-[10px] text-zinc-400 uppercase">{ch.name}</span>
                </div>
                <span className="font-mono text-[10px] text-zinc-300 font-bold">{ch.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2: Top Intents + Response Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart: Top Intents */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.top_intents')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">{t('analytics_dashboard.most_frequent_queries')}</p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={topIntents} chartName="Top Intents" period={period} channel={channel} />
              <BarChart3 className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={topIntents.slice(0, 5)}
              layout="vertical"
              margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                width={110}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name={t('analytics_dashboard.queries')} fill="#f59e0b" radius={[0, 4, 4, 0]}>
                {topIntents.slice(0, 5).map((_, index) => (
                  <Cell key={`bar-${index}`} fillOpacity={1 - index * 0.15} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Area Chart: Response Time */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.response_time')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">Average response time (ms)</p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={responseData} chartName="Response Time" period={period} channel={channel} />
              <Zap className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={responseData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorP95" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}
              />
              <Area
                type="monotone"
                dataKey="avgMs"
                name={t('analytics_dashboard.avg_ms')}
                stroke="#f59e0b"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorAvg)"
              />
              <Area
                type="monotone"
                dataKey="p95Ms"
                name={t('analytics_dashboard.p95_ms')}
                stroke="#0ea5e9"
                strokeWidth={1.5}
                fillOpacity={1}
                fill="url(#colorP95)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 3: Hourly Distribution + Weekly Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.hourly_distribution')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">{t('analytics_dashboard.messages_by_hour_of_day')}</p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={hourlyDistribution} chartName="Hourly Distribution" period={period} channel={channel} />
              <Clock className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourlyDistribution} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis
                dataKey="hour"
                tick={{ fill: "#71717a", fontSize: 9, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                interval={2}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="messages" name={t('analytics_dashboard.messages')} fill="#f59e0b" radius={[2, 2, 0, 0]}>
                {hourlyDistribution.map((entry, index) => (
                  <Cell
                    key={`hour-${index}`}
                    fillOpacity={entry.messages > 200 ? 1 : entry.messages > 100 ? 0.6 : 0.3}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Weekly Comparison */}
        <div className="border border-zinc-800 bg-zinc-950/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.weekly_trend')}</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-1">4-week comparison</p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu data={weeklyComparison} chartName="Weekly Trend" period={period} channel={channel} />
              <TrendingUp className="w-4 h-4 text-zinc-500" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weeklyComparison} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="week"
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "10px", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}
              />
              <Line
                type="monotone"
                dataKey="resolution"
                name={t('analytics_dashboard.resolution')}
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 4, fill: "#10b981" }}
              />
              <Line
                type="monotone"
                dataKey="satisfaction"
                name={t('analytics_dashboard.satisfaction')}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 4, fill: "#f59e0b" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Agent Performance Table */}
      <div className="border border-zinc-800 bg-zinc-950/30">
        <div className="p-5 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div>
              <SectionLabel>// AGENT PERFORMANCE</SectionLabel>
              <h3 className="font-display text-sm uppercase">{t('analytics_dashboard.per_agent_breakdown')}</h3>
            </div>
            <Bot className="w-4 h-4 text-zinc-500" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.agent')}</th>
                <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.role')}</th>
                <th className="text-right px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.messages')}</th>
                <th className="text-right px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.avg_response')}</th>
                <th className="text-right px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.resolution')}</th>
                <th className="text-right px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('analytics_dashboard.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {[
                { name: "Primary Agent", role: "General Ops", messages: 28456, response: "1.1s", resolution: "82.3%", status: "Active" },
                { name: "Support Agent", role: "Support", messages: 6211, response: "1.4s", resolution: "71.8%", status: "Active" },
              ].map((agent, i) => (
                <tr key={i} className="hover:bg-zinc-950/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-amber-500" />
                      </div>
                      <span className="font-display text-sm uppercase">{agent.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-zinc-400">{agent.role}</td>
                  <td className="px-5 py-4 text-right font-mono text-xs text-zinc-300">{formatNumber(agent.messages)}</td>
                  <td className="px-5 py-4 text-right font-mono text-xs text-zinc-300">{agent.response}</td>
                  <td className="px-5 py-4 text-right">
                    <span className="font-mono text-xs text-emerald-400 font-bold">{agent.resolution}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="font-mono text-[10px] text-emerald-500 uppercase">{agent.status}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer note */}
      <div className="text-center py-4 border-t border-zinc-900">
        <p className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.2em]">
          Data refreshed in real-time • Last update: {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
});